import { SETTINGS_GENERAL } from '@/config/settings.general'

import {
    CONTENT_LOCALES,
    CONTENT_LOCALE_CONFIG,
    X_DEFAULT_LOCALE,
    resolveContentLocale,
    type ContentLocale,
} from './contentLocales'
import { getLandingHomePath, isLandingLocale } from './landingLocales'

import type { Locale } from './routing'

function normalizePathname(pathname: string): string {
    if (!pathname || pathname === '/') {
        return ''
    }

    return pathname.startsWith('/') ? pathname : `/${pathname}`
}

function getPublicPrefix(locale: Locale, pathname = ''): string {
    const resolved = resolveContentLocale(locale)
    const path = normalizePathname(pathname)

    if (!path) {
        const home = getLandingHomePath(resolved)
        return home === '/' ? '' : home
    }

    return CONTENT_LOCALE_CONFIG[resolved].prefix
}

/**
 * Absolute (prod) or relative (dev) localized URL on the single canonical host.
 */
export function getLocalizedUrl(locale: Locale, pathname = ''): string {
    const path = normalizePathname(pathname)
    const prefix = getPublicPrefix(locale, pathname)

    if (process.env.NODE_ENV !== 'production') {
        return `${prefix}${path}` || '/'
    }

    return `${SETTINGS_GENERAL.SITE_URL}${prefix}${path}`
}

function localesForAlternates(pathname = ''): readonly ContentLocale[] {
    const path = normalizePathname(pathname)

    if (!path) {
        return CONTENT_LOCALES.filter(isLandingLocale)
    }

    return CONTENT_LOCALES
}

/**
 * hreflang map for a pathname when all locales exist (static pages).
 * For blog articles prefer getArticleAlternateLanguages with published locales only.
 */
export function getAlternateLanguages(pathname = ''): Record<string, string> {
    const languages: Record<string, string> = {}

    for (const locale of localesForAlternates(pathname)) {
        languages[CONTENT_LOCALE_CONFIG[locale].hreflang] = getLocalizedUrl(
            locale,
            pathname
        )
    }

    languages['x-default'] = getLocalizedUrl(X_DEFAULT_LOCALE, pathname)

    return languages
}

/**
 * Reciprocal hreflang for an article: only published locales + self + x-default when EN exists.
 */
export function getArticleAlternateLanguages(
    localePaths: Partial<Record<ContentLocale, string>>
): Record<string, string> {
    const languages: Record<string, string> = {}

    for (const locale of CONTENT_LOCALES) {
        const path = localePaths[locale]
        if (!path) continue
        languages[CONTENT_LOCALE_CONFIG[locale].hreflang] = getLocalizedUrl(
            locale,
            path
        )
    }

    const xDefaultPath = localePaths[X_DEFAULT_LOCALE]
    if (xDefaultPath) {
        languages['x-default'] = getLocalizedUrl(X_DEFAULT_LOCALE, xDefaultPath)
    }

    return languages
}

export function getCanonicalUrl(locale: Locale, pathname = ''): string {
    return getLocalizedUrl(locale, pathname)
}

export function getBlogArticlePath(slug: string): string {
    return `/blog/${slug}`
}
